import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Pencil, ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/button.js';
import { Card, CardContent } from './ui/card.js';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from './ui/drawer.js';
import { allTimezones } from 'react-timezone-select';
import { format, setDay, setHours, setMinutes, isBefore, addWeeks, startOfWeek } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { authGuard } from '../lib/utils.js';
import { useAxios } from './axiosContext.js';
import Toast from './Toast.jsx';
import { AxiosInstance } from 'axios';
import { createPortal } from 'react-dom';

// Helper function to convert 24-hour time to 12-hour AM/PM format
const formatTimeToAMPM = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Generate 12-hour time options in 30-minute increments
const generateTimeOptions = () => {
  const times: string[] = [];
  for (let hour = 1; hour <= 12; hour++) {
    for (let minute of [0, 30]) {
      const timeStr: string = `${hour}:${minute.toString().padStart(2, '0')}`;
      times.push(timeStr);
    }
  }
  return times;
};

// Convert 12-hour time + AM/PM to 24-hour format
const convertTo24Hour = (time12: string, ampm: string) => {
  const [hours, minutes] = time12.split(':');
  let hour = parseInt(hours, 10);
  if (ampm === 'PM' && hour !== 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
};

// Convert 24-hour time to 12-hour format + AM/PM
const convertTo12Hour = (time24: string) => {
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return { time: `${displayHour}:${minutes}`, ampm };
};

// Get next call date based on current schedule
const getNextCallDate = (dayOfWeek: string, time: string, timezone: string) => {
  const now = new Date();
  const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(dayOfWeek.toLowerCase());
  
  // Create a date for this week on the specified day
  let nextCall = setDay(startOfWeek(now), dayIndex);
  
  // Set the time
  const [hours, minutes] = time.split(':');
  nextCall = setHours(nextCall, parseInt(hours, 10));
  nextCall = setMinutes(nextCall, parseInt(minutes, 10));
  
  // If the call time has passed this week, move to next week
  if (isBefore(nextCall, now)) {
    nextCall = addWeeks(nextCall, 1);
  }
  
  return nextCall;
};

// Get all timezones
const getTimezones = () => {
  return Object.entries(allTimezones).map(([value, label]) => ({
    value,
    label: `${label} (${formatInTimeZone(new Date(), value, 'xxx')})`
  }));
};

// Custom Dropdown Component
const CustomDropdown = ({ 
  value, 
  options, 
  onChange, 
  placeholder = "Select option",
  className = "" 
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap (mt-2)
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full p-3 border border-gray-200 rounded-xl bg-white text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 shadow-sm text-left flex items-center justify-between hover:border-gray-300 transition-all duration-200 hover:shadow-md"
        >
          <span className={selectedOption ? "text-gray-900 font-medium" : "text-gray-500"}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown 
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </button>
      </div>

      {/* Render dropdown content in portal at document.body level */}
      {typeof document !== 'undefined' && isOpen && createPortal(
        <AnimatePresence>
          <>
            {/* Backdrop to close dropdown */}
            <div 
              className="fixed inset-0 z-[9998]" 
              onClick={() => setIsOpen(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed z-[9999] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width
              }}
            >
              <div className="max-h-60 overflow-y-auto">
                {options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                    className={`w-full px-3 py-2.5 text-sm text-left hover:bg-green-50 transition-colors flex items-center justify-between ${
                      value === option.value ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    <span>{option.label}</span>
                    {value === option.value && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export const CheckInSchedule: React.FC = () => {
  const axios : AxiosInstance = useAxios();
  const auth = authGuard();
  const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false);
  const [showScheduleSetup, setShowScheduleSetup] = useState(false);
  const [isTimezoneEditing, setIsTimezoneEditing] = useState(false);
  const [scheduleDay, setScheduleDay] = useState('monday');
  const [scheduleTime, setScheduleTime] = useState('09:30');
  const [scheduleTime12, setScheduleTime12] = useState('9:30');
  const [scheduleAmPm, setScheduleAmPm] = useState('AM');
  const [selectedTimezone, setSelectedTimezone] = useState('Australia/Sydney');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    message: '',
    variant: 'success' as 'success' | 'error',
  });
  // Add new state for existing schedule data
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(true);
  const [hasExistingSchedule, setHasExistingSchedule] = useState(false);
  const [existingScheduleId, setExistingScheduleId] = useState<string | null>(null);
  
  const timeOptions = generateTimeOptions();
  const timezones = getTimezones();
  
  // Dropdown options
  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const timeDropdownOptions = timeOptions.map(time => ({
    value: time,
    label: time
  }));

  const timezoneDropdownOptions = timezones.map(tz => ({
    value: tz.value,
    label: tz.label
  }));

  // Function to extract day of week from date
  const getDayOfWeekFromDate = (date: Date): string => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  // Function to extract time from date
  const getTimeFromDate = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Fetch existing recurring schedule data
  const fetchExistingSchedule = async () => {
    try {
      setIsLoadingSchedule(true);
      
      if (!auth?.user?.isSignedIn) {
        throw new Error('User not authenticated');
      }

      const response = await axios.get('/recurring-calls',{
        params: {
          callType: 'FOLLOWUP',
          limit: 1,
          offset: 0,
          sort: 'createdAt',
          order: 'desc',
          frequency: 'WEEKLY',
        }
      });
      
      if (response.data && response.data.length > 0) {
        // Get the first (most recent) recurring schedule
        const schedule = response.data[0];
        setHasExistingSchedule(true);
        setExistingScheduleId(schedule.id);
        
        // Extract timezone from startDate (assuming it's in the user's timezone)
        // For now, we'll use the current timezone or try to detect from user metadata
        const startDate = new Date(schedule.startDate);
        
        // Extract day of week from start date
        const dayOfWeek = getDayOfWeekFromDate(startDate);
        setScheduleDay(dayOfWeek);
        
        // Extract time from start date
        const time24 = getTimeFromDate(startDate);
        setScheduleTime(time24);
        
        // Convert to 12-hour format
        const { time, ampm } = convertTo12Hour(time24);
        setScheduleTime12(time);
        setScheduleAmPm(ampm);
        
        // TODO: Extract timezone from user preferences or schedule data
        // For now, keep the default timezone
        
      } else {
        // No existing schedule - use current date as fallback
        setHasExistingSchedule(false);
        setExistingScheduleId(null);
        
        const now = new Date();
        const currentDay = getDayOfWeekFromDate(now);
        const currentTime = getTimeFromDate(now);
        
        setScheduleDay(currentDay);
        setScheduleTime(currentTime);
        
        const { time, ampm } = convertTo12Hour(currentTime);
        setScheduleTime12(time);
        setScheduleAmPm(ampm);
      }
      
    } catch (error) {
      console.error('Failed to fetch existing schedule:', error);
      // On error, use current date as fallback
      setHasExistingSchedule(false);
      setExistingScheduleId(null);
      
      const now = new Date();
      const currentDay = getDayOfWeekFromDate(now);
      const currentTime = getTimeFromDate(now);
      
      setScheduleDay(currentDay);
      setScheduleTime(currentTime);
      
      const { time, ampm } = convertTo12Hour(currentTime);
      setScheduleTime12(time);
      setScheduleAmPm(ampm);
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  // Fetch existing schedule on component mount
  React.useEffect(() => {
    if (auth?.user?.isSignedIn) {
      fetchExistingSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.isSignedIn]);
  
  // Update 24-hour time when 12-hour time or AM/PM changes
  React.useEffect(() => {
    const newTime24 = convertTo24Hour(scheduleTime12, scheduleAmPm);
    setScheduleTime(newTime24);
  }, [scheduleTime12, scheduleAmPm]);

  // Initialize 12-hour format from default 24-hour time (only if not loading)
  React.useEffect(() => {
    if (!isLoadingSchedule) {
      const { time, ampm } = convertTo12Hour(scheduleTime);
      setScheduleTime12(time);
      setScheduleAmPm(ampm);
    }
  }, [scheduleTime, isLoadingSchedule]);

  const nextCall = getNextCallDate(scheduleDay, scheduleTime, selectedTimezone);

  const handleSaveSchedule = async () => {
    try {
      setIsLoading(true);

      if(!auth?.user?.isSignedIn){
        throw new Error('User not authenticated');
      }
      
      // Get the next occurrence of the selected day
      const nextCall = getNextCallDate(scheduleDay, scheduleTime, selectedTimezone);
      
      // Create the start date with the correct time
      const [hours, minutes] = scheduleTime.split(':');
      const startDate = new Date(nextCall);
      startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      
      // Convert the start date from the user's timezone to UTC
      const utcStartDate = toZonedTime(startDate, selectedTimezone);
      
      // Set endDate to the last date of the current month, keeping the time the same as startDate
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 1, 0); // Move to last day of current month
      
      // Convert end date to UTC
      const utcEndDate = toZonedTime(endDate, selectedTimezone);
      
      // Create the payload for recurring call
      const payload = {
        startDate: utcStartDate.toISOString(),
        endDate: utcEndDate.toISOString(),
        frequency: 'WEEKLY', // Weekly recurring calls
        callType: 'FOLLOWUP',
      };
      
      // If existing schedule, update it; otherwise create new one
      if (hasExistingSchedule && existingScheduleId) {
        await axios.patch(`/recurring-calls/${existingScheduleId}?editMode=all`, payload);
      } else {
        await axios.post('/recurring-calls', payload);
      }
      
      // Refetch the schedule data after saving
      await fetchExistingSchedule();
      
      // Success - close modals and show success message
      setIsScheduleDrawerOpen(false);
      setShowScheduleSetup(false);
      setToastMessage({
        message: 'Check-in call schedule saved successfully!',
        variant: 'success',
      });
      setShowToast(true);
      
    } catch (error) {
      // Handle error
      console.error('Failed to save schedule:', error);
      setToastMessage({
        message: 'Failed to save check-in call schedule',
        variant: 'error',
      });
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while fetching schedule data
  if (isLoadingSchedule) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card className="rounded-xl border border-green-200/40 shadow-lg shadow-green-900/5 bg-gradient-to-br from-green-50/60 to-green-100/40 backdrop-blur-md">
          <CardContent className="p-3 sm:p-4">
            <h2 className="text-lg sm:text-xl font-semibold text-hana-dark-green mb-3">Check-in Call Schedule</h2>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Phone className="h-4 w-4 text-green-600" />
              </div>
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card className="rounded-xl border border-green-200/40 shadow-lg shadow-green-900/5 bg-gradient-to-br from-green-50/60 to-green-100/40 backdrop-blur-md">
        <CardContent className="p-3 sm:p-4">
          {/* Heading */}
          <h2 className="text-lg sm:text-xl font-semibold text-hana-dark-green mb-3">Check-in Call Schedule</h2>
          
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-start gap-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <Phone className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-hana-dark-green text-sm sm:text-base">
                  {format(nextCall, 'EEEE, do MMMM')}
                </h3>
                <h3 className="font-medium text-hana-dark-green text-sm sm:text-base">
                  {formatTimeToAMPM(scheduleTime)}
                </h3>
                <p className="text-xs text-green-600/80">Every {scheduleDay} at {formatTimeToAMPM(scheduleTime)}</p>
              </div>
            </div>
            
            {/* Mobile Edit Button */}
            <div className="sm:hidden">
              <Drawer open={isScheduleDrawerOpen} onOpenChange={setIsScheduleDrawerOpen}>
                <DrawerTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-green-600 hover:bg-green-100 rounded-full p-2">
                    <Pencil className="h-4 w-4" />
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="max-h-[90vh]">
                  <DrawerHeader>
                    <DrawerTitle className="text-xl font-semibold text-hana-dark-green">Setup Recurring Check-in Calls</DrawerTitle>
                  </DrawerHeader>
                  <div className="p-6 space-y-6">
                    {/* Day */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Day</label>
                      <CustomDropdown
                        value={scheduleDay}
                        options={dayOptions}
                        onChange={setScheduleDay}
                        placeholder="Select day"
                      />
                    </div>

                    {/* Time */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Time</label>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <CustomDropdown
                            value={scheduleTime12}
                            options={timeDropdownOptions}
                            onChange={setScheduleTime12}
                            placeholder="Select time"
                          />
                        </div>
                        
                        {/* AM/PM Toggle - Compact */}
                        <div className="flex bg-gray-100 rounded-lg p-0.5">
                          <button
                            type="button"
                            onClick={() => setScheduleAmPm('AM')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                              scheduleAmPm === 'AM' 
                                ? 'bg-hana-dark-green text-white shadow-sm' 
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            AM
                          </button>
                          <button
                            type="button"
                            onClick={() => setScheduleAmPm('PM')}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                              scheduleAmPm === 'PM' 
                                ? 'bg-hana-dark-green text-white shadow-sm' 
                                : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            PM
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Timezone */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-base font-semibold text-gray-800">Timezone</label>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-green-600 hover:bg-green-100 rounded-full p-1 h-6 w-6"
                          onClick={() => setIsTimezoneEditing(!isTimezoneEditing)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </div>
                      {isTimezoneEditing ? (
                        <CustomDropdown
                          value={selectedTimezone}
                          options={timezoneDropdownOptions}
                          onChange={(value) => {
                            setSelectedTimezone(value);
                            setIsTimezoneEditing(false);
                          }}
                          placeholder="Select timezone"
                        />
                      ) : (
                        <div className="text-sm text-gray-700">
                          {timezones.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone}
                        </div>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        className="flex-1 rounded-xl"
                        onClick={() => setIsScheduleDrawerOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        className="flex-1 bg-hana-dark-green hover:bg-hana-dark-green/90 text-white rounded-xl"
                        onClick={handleSaveSchedule}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Saving...' : 'Save Schedule'}
                      </Button>
                    </div>
                  </div>
                </DrawerContent>
              </Drawer>
            </div>

            {/* Desktop Edit Button */}
            <div className="hidden sm:block">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-green-600 hover:bg-green-100 rounded-full p-2"
                onClick={() => setShowScheduleSetup(!showScheduleSetup)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Desktop Setup Section */}
          {showScheduleSetup && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="hidden sm:block mt-6 pt-6 border-t border-green-200/50"
            >
              <h4 className="font-medium text-hana-dark-green mb-6">Setup Recurring Check-in Calls</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Day */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Day</label>
                  <CustomDropdown
                    value={scheduleDay}
                    options={dayOptions}
                    onChange={setScheduleDay}
                    placeholder="Select day"
                  />
                </div>

                {/* Time */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Time</label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <CustomDropdown
                        value={scheduleTime12}
                        options={timeDropdownOptions}
                        onChange={setScheduleTime12}
                        placeholder="Select time"
                      />
                    </div>
                    
                    {/* AM/PM Toggle - Compact */}
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setScheduleAmPm('AM')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                          scheduleAmPm === 'AM' 
                            ? 'bg-hana-dark-green text-white shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        AM
                      </button>
                      <button
                        type="button"
                        onClick={() => setScheduleAmPm('PM')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                          scheduleAmPm === 'PM' 
                            ? 'bg-hana-dark-green text-white shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        PM
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timezone */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Timezone</label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-green-600 hover:bg-green-100 rounded-full p-1 h-6 w-6"
                    onClick={() => setIsTimezoneEditing(!isTimezoneEditing)}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
                {isTimezoneEditing ? (
                  <CustomDropdown
                    value={selectedTimezone}
                    options={timezoneDropdownOptions}
                    onChange={(value) => {
                      setSelectedTimezone(value);
                      setIsTimezoneEditing(false);
                    }}
                    placeholder="Select timezone"
                  />
                ) : (
                  <span className="text-sm text-gray-700">
                    {timezones.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone}
                  </span>
                )}
              </div>

              {/* Save Button */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="rounded-xl"
                  onClick={() => setShowScheduleSetup(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="bg-hana-dark-green hover:bg-hana-dark-green/90 text-white rounded-xl"
                  onClick={handleSaveSchedule}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Schedule'}
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>
      
      {/* Toast for success/error messages */}
      {showToast && (
        <Toast
          message={toastMessage.message}
          variant={toastMessage.variant}
          setShowToast={setShowToast}
        />
      )}
    </motion.div>
  );
};